import { createContext, useContext, useState, useMemo, useRef, useEffect } from 'react';
import * as React from 'react';
import { UpdateUserSettings, UserSettings } from '@sdk/authentication/types';

type UserSettingsContextType = {
  userSettings: UserSettings;
  isUserSettingsChanged: boolean;
  onUserSettingsChange: UpdateUserSettings;
  revertUserSettingsChanges: () => void;
  setInitialUserSettings: (settigs: UserSettings) => void;
  setIsUserSettingsChanged: (value: boolean) => void;
  initialUserSettings: UserSettings;
};
const UserSettingContext = createContext<UserSettingsContextType>({
  userSettings: null,
  isUserSettingsChanged: null,
  onUserSettingsChange: null,
  revertUserSettingsChanges: null,
  setInitialUserSettings: null,
  setIsUserSettingsChanged: null,
  initialUserSettings: null,
});

type Props = {
  settings: UserSettings;
};
const UserSettingsProvider: React.FC<React.PropsWithChildren<Props>> = ({ children, settings }) => {
  const initialUserSettings = useRef(settings);
  const [userSettings, setUserSettings] = useState<UserSettings>(settings);
  const [isUserSettingsChanged, setIsUserSettingsChanged] = useState(false);

  useEffect(() => {
    setUserSettings(settings);
    initialUserSettings.current = settings;
  }, [settings]);

  useEffect(() => {
    setIsUserSettingsChanged(
      JSON.stringify(initialUserSettings.current) !== JSON.stringify(userSettings)
    );
  }, [userSettings]);

  const value = useMemo(() => {
    const onUserSettingsChange: UpdateUserSettings = (updatedSettingsProperties): void => {
      const updatedUserSettings = {
        ...userSettings,
        ...updatedSettingsProperties,
      };

      setUserSettings(updatedUserSettings);
    };

    const revertUserSettingsChanges = (): void => setUserSettings(initialUserSettings.current);

    const setInitialUserSettings = (value: UserSettings): UserSettings =>
      (initialUserSettings.current = value);

    return {
      userSettings,
      isUserSettingsChanged,
      onUserSettingsChange,
      revertUserSettingsChanges,
      setInitialUserSettings,
      setIsUserSettingsChanged,
      initialUserSettings: initialUserSettings.current,
    };
  }, [userSettings, isUserSettingsChanged]);

  return <UserSettingContext.Provider value={value}>{children}</UserSettingContext.Provider>;
};

function useUserSettings(): UserSettingsContextType {
  const context = useContext(UserSettingContext);
  if (!context) {
    throw new Error('UserSettingContext must be used within a UserSettingsProvider');
  }

  return context;
}

export { UserSettingsProvider, useUserSettings };
